import * as React from 'react';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import { useState } from 'react';
import { MenuItem, FormControl, FormHelperText, InputLabel, Box, Button, Checkbox, FormControlLabel } from '@mui/material';
import Select from '@mui/material/Select';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import CreatableSelect from 'react-select/creatable';
import { useData, resolveFullTimeProducts } from '../Form';
import { useStep } from '../Form';
import BackAndNextButtons from '../components/BackAndNextButtons';
import { useParams } from '../components/ParamsContext';
import { useConferenceConfig } from '../components/ConferenceConfigContext';

const filter = createFilterOptions();

export default function InformationForm() {
  const { data, updateData } = useData();
  const { incrementStep } = useStep();
  const { noLateFee } = useParams();
  const { config } = useConferenceConfig();

  const [values, setValues] = useState({
    firstName: data.firstName ?? "",  // Use data.firstName if available, otherwise ""
    lastName: data.lastName ?? "",
    email: data.email ?? "",
    phoneNumber: data.phoneNumber ?? "",
    gender: data.gender ?? "",
    status: data.status ?? "",
    school: data.school ?? "",
    city: data.city ?? "",
    allergies: data.allergies ?? [], // Use an empty array if no allergies data is available
    hospitality: data.hospitality ?? false,
    firstTime: data.firstTime ?? false
  });

  const [error, setError] = useState({
    firstName: false,
    lastName: false,
    email: false,
    phoneNumber: false,
    gender: false,
    status: false,
    school: false,
    city: false,
    allergies: false,
    quarterSystem: false,
  });

  const campuses = [
    'Butte College', 'Cal Poly SLO', 'Chico State', 'City College of San Francisco', 'CSU East Bay', 'De Anza', 'Foothill Community College',
    'Fresno State', 'Merced College', 'Modesto Junior College', 'Northeastern University', 'Santa Clara University', 'Sacramento State',
    'San Francisco State University', 'San Jose State', 'Sierra College', 'Stanford University', 'Sonoma State University',
    'UC Berkeley', 'UC Davis', 'UC Merced', 'UC Santa Cruz', 'UNR', 'University of San Francisco', 'West Valley College'
  ];

  const statusList = [
    'High School Senior', 'Freshman', 'Sophomore', 'Junior', 'Senior', 'Grad Student', 'Recent College Graduate', 'FT', 'Serving One', 'Other'
  ];

  const cities = [
    'Alameda', 'Auburn', 'Bakersfield', 'Berkeley', 'Burlingame', 'Brentwood', 'Campbell', 'Chico', 'Crescent City',
    'Cupertino', 'Davis', 'Elk Grove', 'Eureka', 'Folsom', 'Fremont', 'Fresno', 'Hayward', 'Merced', 'Modesto', 'Monterey',
    'Morgan Hill', 'Mountain View', 'Oakland', 'Palo Alto', 'Pleasant Hill', 'Pleasanton', 'Redding', 'Reno', 'Rohnert Park',
    'Roseville', 'Sacramento', 'Salinas', 'San Francisco', 'San Jose', 'San Luis Obispo', 'Santa Clara', 'Santa Cruz',
    'Stockton', 'Sunnyvale', 'Tulare', 'Union City', 'Vallejo'
  ];

  const allergyOptions = [
    { value: 'dairy free', label: 'dairy free' }, { value: 'gluten free', label: 'gluten free' }, { value: 'nut free', label: 'nut free' },{ value: 'vegetarian', label: 'vegetarian' },
  ];

  function checkForErrors() {
    let newError = {
      firstName: values.firstName === "",
      lastName: values.lastName === "",
      email: values.email === "" || !(/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i).test(values.email),
      phoneNumber: values.phoneNumber === "" || !(/^(\+\d{1,2}\s?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/i).test(values.phoneNumber),
      gender: (values.gender === undefined) || (values.gender === null) || (values.gender === ""),
      status: (values.status === undefined) || (values.status === null) || (values.status === "") || !(statusList.includes(values.status)),
      school: (values.school === undefined) || (values.school === null) || (values.school === ""),
      city: (values.status === "High School Senior" && values.city === "") ? true : false
    };
    setError(newError);
    return Object.keys(newError).some((k) => newError[k]);
  }

  function handleNext() {
    if (checkForErrors()) {
      // If there are errors, don't proceed
      return;
    } else {
      incrementStep(); // Proceed to the next step
      let updatedValues = values;
      // Full time (no parttime link) doesn't know its price until now - see
      // Form.js's isDeferredFullTime. Every other participation type already
      // has its products resolved and is left untouched.
      if (data.participation === 'FT') {
        const { participation, products } = resolveFullTimeProducts(values.status, config, noLateFee);
        updatedValues = {
          ...values,
          participation,
          products: values.firstTime ? products.filter((p) => p.name !== 'Late Fee') : products,
        };
      }
      updateData(updatedValues); // Save the current form data
    }
  }

  function isHighSchoolSenior() {
    return values.status === 'High School Senior';
  }


  return (
    <React.Fragment>
      <Typography variant="h6" gutterBottom>
        Personal Information
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <TextField
            required
            error={error.firstName}
            helperText={error.firstName ? "Cannot be left blank" : ''}
            id="firstName"
            name="firstName"
            label="First name"
            fullWidth
            autoComplete="given-name"
            variant="standard"
            value={values.firstName}
            onChange={(e) => {
              setValues({ ...values, ...{ firstName: e.target.value } })
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            required
            error={error.lastName}
            helperText={error.lastName ? "Cannot be left blank" : ''}
            id="lastName"
            name="lastName"
            label="Last name"
            fullWidth
            autoComplete="family-name"
            variant="standard"
            value={values.lastName}
            onChange={(e) => {
              setValues({ ...values, ...{ lastName: e.target.value } })
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            required
            error={error.email}
            helperText={error.email ? "Invalid email" : ''}
            id="email"
            name="email"
            label="Email"
            fullWidth
            autoComplete="email"
            variant="standard"
            value={values.email}
            onChange={(e) => {
              setValues({ ...values, ...{ email: e.target.value } })
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            required
            error={error.phoneNumber}
            helperText={error.phoneNumber ? "Invalid phone number" : ''}
            id="phone"
            name="phone"
            label="Phone Number"
            fullWidth
            autoComplete="tel-national"
            variant="standard"
            value={values.phoneNumber}
            onChange={(e) => {
              setValues({ ...values, ...{ phoneNumber: e.target.value } })
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth size="small" required>
            <InputLabel error={error.gender} id="gender-label">Gender</InputLabel>
            <Select
              required
              labelid='gender-label'
              id="gender"
              error={error.gender}
              value={values.gender}
              label="gender"
              onChange={(e) => {
                setValues({ ...values, ...{ gender: e.target.value } })
              }}
            >
              <MenuItem value={'M'}>M</MenuItem>
              <MenuItem value={'F'}>F</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth size="small" required>
            <InputLabel error={error.status}
              id="status-label">Status
            </InputLabel>
            <Select
              required
              error={error.status}
              labelid='status-label'
              id="status"
              value={values.status}
              label="status"
              onChange={(e) => {
                setValues({ ...values, ...{ status: e.target.value } })
              }}
            >
              {statusList.map((s, index) => (
                <MenuItem key={index} value={s}>{s}</MenuItem> // Provide a unique key here
              ))}
            </Select>
          </FormControl>
        </Grid>
        {isHighSchoolSenior() ?
          <Grid item xs={12}>
            <FormControl fullWidth size="small" required error={error.city}>
              <InputLabel id="city">City</InputLabel>
              <Select
                required
                labelid="city"
                id="city"
                value={values.city}
                label="City"
                onChange={(e) => {
                  setValues({ ...values, ...{ city: e.target.value } })

                }}
              >
                {cities.map((c) => {
                  return (
                    <MenuItem value={c} key={c}>{c}</MenuItem>)
                })}
              </Select>
              <FormHelperText>Please select which city you are from</FormHelperText>
            </FormControl>
          </Grid>
          : <></>}
        <Grid item xs={12}>
          <FormControl fullWidth size="small" required>
            <Autocomplete
              required
              value={values.school}
              onChange={(event, newValue) => {
                if (typeof newValue === 'string') {
                  setValues({ ...values, ...{ school: newValue } });
                } else if (newValue && values.inputValue) {
                  // Create a new value from the user input
                  setValues({ ...values, ...{ school: values.inputValue } });
                } else {
                  setValues({ ...values, ...{ school: newValue } });
                }
              }}
              filterOptions={(options, params) => {
                const filtered = filter(options, params);

                const { inputValue } = params;
                // Suggest the creation of a new value
                const isExisting = options.some((option) => inputValue === option);
                if (inputValue !== '' && !isExisting) {
                  filtered.push(
                    inputValue,
                  );
                }

                return filtered;
              }}
              selectOnFocus
              clearOnBlur
              handleHomeEndKeys
              id="campus"
              options={campuses}
              freeSolo
              renderInput={(params) => (
                <TextField {...params} error={error.school} label="School" required={true} />
              )}
            />
            <FormHelperText>If your school is not listed, please type in your school. Serving Ones: please select the campus you are affiliated with</FormHelperText>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
                  <Typography variant="h6">
        Special meal requests for food allergies or dietary restrictions
      </Typography>
          <CreatableSelect
            isMulti
            options={allergyOptions}
            value={values.allergies}
            placeholder={`Please select from the following options`}
            onChange={(newValue) => {
              setValues({ ...values, ...{ allergies: newValue } })
            }}
          />
        </Grid>
        {/* <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox checked={values.hospitality}
                onChange={() => {
                  setValues({ ...values, ...{ hospitality: !values.hospitality } }
                  )
                }
                } />}
            label="I am traveling from outside the East Bay, South Bay, and the Peninsula and need lodging for one night" />
        </Grid> */}
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox checked={values.firstTime}
                onChange={() => {
                  let firstTime = !values.firstTime;

                  if (firstTime) {
                    const lateFeeIndex = data.products.findIndex(obj => obj.name === 'Late Fee');

                    if (lateFeeIndex !== -1) {
                      // Create a new array without the late fee item
                      const updatedProducts = [...data.products];
                      updatedProducts.splice(lateFeeIndex, 1);

                      // Update the data object (assuming it's state)
                      updateData({ ...data, products: updatedProducts });
                    }
                  }

                  setValues({ ...values, firstTime });
                }} />}
            label="This is my first College Conference" />
        </Grid>
      </Grid>
      <BackAndNextButtons handleNext={handleNext}></BackAndNextButtons>


    </React.Fragment>
  );
}
